// src/components/AIAgentsList.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaRobot, FaTools, FaEnvelope, FaCheck, FaTimes, FaWhatsapp, FaFacebook, FaTwitter, FaSlack, FaInstagram, FaDiscord, FaTelegram, FaAmazon, FaComments, FaPlus, FaTag } from 'react-icons/fa';
import './AIAssistant.css';
import { useNavigate } from 'react-router-dom';
import Button from './UI/Button';

const AIAgentsList = ({ onSelectAgent }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [aiAssistants, setAIAssistants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [availableTools, setAvailableTools] = useState([]);
  const [userEmails, setUserEmails] = useState({
    gmail_email: null,
    outlook_emails: []
  });
  const [availableChannels, setAvailableChannels] = useState({});
  const [showEmailSelector, setShowEmailSelector] = useState(false);
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [selectedAssistantForEmail, setSelectedAssistantForEmail] = useState(null);
  const [selectedAssistantForChannels, setSelectedAssistantForChannels] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState({});
  const [associationLoading, setAssociationLoading] = useState(false);
  const [associationSuccess, setAssociationSuccess] = useState(null);
  const [channelAssociationLoading, setChannelAssociationLoading] = useState(false);
  const [channelAssociationSuccess, setChannelAssociationSuccess] = useState(null);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentPrompt, setNewAgentPrompt] = useState('');
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null);
  
  // Brand association state
  const [availableBrands, setAvailableBrands] = useState([]);
  const [showBrandSelector, setShowBrandSelector] = useState(false);
  const [selectedAssistantForBrand, setSelectedAssistantForBrand] = useState(null);
  const [brandAssociationLoading, setBrandAssociationLoading] = useState(false);
  const [brandAssociationSuccess, setBrandAssociationSuccess] = useState(null);

  // Dummy Data for "experience" role
  const dummyAIAssistants = [
    { _id: 'exp_agent_1', name: 'Support Pro Agent (Experience)', prompt: 'You are a helpful support agent. Prioritize clarity and quick resolutions.', selected_tool_ids: ['tool_exp_1', 'tool_exp_2'], associated_email: 'experience_user@gmail.com', associated_channels: { 'whatsapp': true, 'website': false } },
    { _id: 'exp_agent_2', name: 'Sales Assistant Bot (Experience)', prompt: 'Your goal is to guide users to the best products and assist with sales inquiries.', selected_tool_ids: ['tool_exp_3'], associated_channels: { 'messenger': true } },
    { _id: 'exp_agent_3', name: 'Tech Troubleshooter (Experience)', prompt: 'Help users with technical issues and guide them through troubleshooting steps.', selected_tool_ids: [], associated_email: 'experience_user_outlook@example.com' },
  ];
  const dummyAvailableTools = [
    { id: 'tool_exp_1', name: 'retrieve_knowledge' },
    { id: 'tool_exp_2', name: 'resolve_ticket' },
    { id: 'tool_exp_3', name: 'getOrderById' },
  ];
  const dummyUserEmails = {
    gmail_email: 'experience_user@gmail.com',
    outlook_emails: ['experience_user_outlook@example.com']
  };
  const dummyAvailableChannels = {
    'whatsapp': true,
    'messenger': true,
    'website': true,
    'instagram': false,
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      loadInitialData(token);
    } else {
      navigate('/');
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 422)) {
        localStorage.removeItem('access_token');
        navigate('/');
      }
      return null;
    }
  };

  const loadInitialData = async (token) => {
    setIsLoading(true);
    const fetchedUser = await fetchUserData(token);
    setUser(fetchedUser);

    if (fetchedUser && fetchedUser.role === 'experience') {
      setAIAssistants(dummyAIAssistants);
      setAvailableTools(dummyAvailableTools);
      setUserEmails(dummyUserEmails);
      setAvailableChannels(dummyAvailableChannels);
      setIsLoading(false);
    } else if (fetchedUser) {
      await Promise.all([
        fetchAssistantsAndTools(token),
        fetchUserEmailsData(token),
        fetchAvailableChannelsData(token),
        fetchAvailableBrandsData(token)
      ]);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };
  
  // (The old DOM-measured SVG "connections" diagram was removed: agent
  // bindings are now shown directly on each card as chips.)

  const fetchAssistantsAndTools = async (token) => {
    try {
      const [assistantsResponse, toolsResponse] = await Promise.all([
        axios.get('/api/ai-assistants', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get('/api/tools', {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);
      
      setAIAssistants(assistantsResponse.data);
      setAvailableTools(toolsResponse.data);
    } catch (error) {
      console.error('Failed to fetch assistants and tools:', error);
       if (error.response && (error.response.status === 401 || error.response.status === 422)) {
        localStorage.removeItem('access_token');
        navigate('/');
      }
    }
  };

  const fetchUserEmailsData = async (token) => {
    try {
      const response = await axios.get('/api/users/user/emails', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserEmails(response.data);
    } catch (error) {
      console.error('Failed to fetch user emails:', error);
    }
  };

  const fetchAvailableChannelsData = async (token) => {
    try {
      const response = await axios.get('/api/users/available-channels', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableChannels(response.data.channels || {});
    } catch (error) {
      console.error('Failed to fetch available channels:', error);
    }
  };

  const fetchAvailableBrandsData = async (token) => {
    try {
      const response = await axios.get('/api/brands/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableBrands(response.data);
    } catch (error) {
      console.error('Failed to fetch available brands:', error);
    }
  };

  const getPromptPreview = (text) => {
    const maxLength = 80;
    return text && text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getToolTranslation = (toolName) => {
    const toolTranslations = {
      'retrieve_knowledge': t('tools.retrieveKnowledge'),
      'assign_to_employee': t('tools.assignToEmployee'),
      'resolve_ticket': t('tools.resolveTicket'),
      'Close_ticket': t('tools.closeTicket'),
      'get_tracking_info': t('tools.getTrackingInfo'),
      'Retrieve_customer_order_by_reference_and_email': t('tools.retrieveOrder'),
      'Update_customer_shipping_address': t('tools.updateShippingAddress'),
      'find_relay_points': t('tools.findRelayPoints'),
      'create_return_with_edn_label': t('tools.createReturn'),
      'carriers_conversation': t('tools.carriersConversation'),
      'Retour': t('tools.return'),
      'fill_form_fields': t('tools.fillPDF'),
      'getOrderById': t('tools.getOrderById'),
      'carriers_conversation_update': t('tools.carriersConversationUpdate'),
      'order_status': t('tools.orderStatus'),
      'Request_return_order': t('tools.Request_return_order'),
      'Retrieve_customer_order_by_ID': t('tools.Retrieve_customer_order_by_ID'),
      'Request_exchange_shopify': t('tools.Request_exchange_shopify'),
      'Get_all_product_variants': t('tools.Get_all_product_variants')
    };
    return toolTranslations[toolName] || toolName;
  };

  const handleAssociateEmailClick = (assistantId) => {
    setSelectedAssistantForEmail(assistantId);
    setShowEmailSelector(true);
  };

  const associateEmailWithAssistant = async (email) => {
    if (!selectedAssistantForEmail) return;
    
    setAssociationLoading(true);
    setAssociationSuccess(null);

    if (user && user.role === 'experience') {
      setTimeout(() => {
        setAIAssistants(prevAssistants => prevAssistants.map(assistant => {
          if (assistant._id === selectedAssistantForEmail) {
            return { ...assistant, associated_email: email };
          }
          return assistant;
        }));
        setAssociationSuccess(true);
        setAssociationLoading(false);
        setTimeout(() => {
          setShowEmailSelector(false);
          setSelectedAssistantForEmail(null);
          setAssociationSuccess(null);
        }, 1500);
      }, 500);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/associate-email',
        {
          email: email,
          assistant_id: selectedAssistantForEmail
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === selectedAssistantForEmail) {
          return { ...assistant, associated_email: email };
        }
        return assistant;
      }));
      
      setAssociationSuccess(true);
      
      setTimeout(() => {
        setShowEmailSelector(false);
        setSelectedAssistantForEmail(null);
        setAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to associate email with assistant:', error);
      setAssociationSuccess(false);
      
      setTimeout(() => {
        setAssociationSuccess(null);
      }, 3000);
    } finally {
      setAssociationLoading(false);
    }
  };

  const removeEmailAssociation = async (assistantId, emailToRemove) => {
    setAssociationLoading(true);
    setAssociationSuccess(null);

    if (user && user.role === 'experience') {
      setTimeout(() => {
        setAIAssistants(prevAssistants => prevAssistants.map(assistant => {
          if (assistant._id === assistantId) {
            const updatedAssistant = { ...assistant };
            delete updatedAssistant.associated_email;
            return updatedAssistant;
          }
          return assistant;
        }));
        setAssociationSuccess(true);
        setAssociationLoading(false);
        setTimeout(() => {
          setShowEmailSelector(false);
          setSelectedAssistantForEmail(null);
          setAssociationSuccess(null);
        }, 1500);
      }, 500);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/remove-email-association',
        {
          email: emailToRemove,
          assistant_id: assistantId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === assistantId) {
          const updatedAssistant = { ...assistant };
          delete updatedAssistant.associated_email;
          return updatedAssistant;
        }
        return assistant;
      }));
      
      setAssociationSuccess(true);
      
      setTimeout(() => {
        setShowEmailSelector(false);
        setSelectedAssistantForEmail(null);
        setAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to remove email association:', error);
      setAssociationSuccess(false);
      
      setTimeout(() => {
        setAssociationSuccess(null);
      }, 3000);
    } finally {
      setAssociationLoading(false);
    }
  };

  const handleAssociateChannelsClick = (assistant) => {
    setSelectedAssistantForChannels(assistant._id);
    const currentAssistant = aiAssistants.find(a => a._id === assistant._id);
    setSelectedChannels(currentAssistant?.associated_channels || {});
    setShowChannelSelector(true);
  };

  const associateChannelsWithAssistant = async () => {
    if (!selectedAssistantForChannels) return;
    
    setChannelAssociationLoading(true);
    setChannelAssociationSuccess(null);

    if (user && user.role === 'experience') {
      setTimeout(() => {
        setAIAssistants(prevAssistants => prevAssistants.map(assistant => {
          if (assistant._id === selectedAssistantForChannels) {
            return { ...assistant, associated_channels: selectedChannels };
          }
          return assistant;
        }));
        setChannelAssociationSuccess(true);
        setChannelAssociationLoading(false);
        setTimeout(() => {
          setShowChannelSelector(false);
          setSelectedAssistantForChannels(null);
          setChannelAssociationSuccess(null);
        }, 1500);
      }, 500);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/associate-channels',
        {
          assistant_id: selectedAssistantForChannels,
          channels: selectedChannels
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === selectedAssistantForChannels) {
          return { ...assistant, associated_channels: selectedChannels };
        }
        return assistant;
      }));
      
      setChannelAssociationSuccess(true);
      
      setTimeout(() => {
        setShowChannelSelector(false);
        setSelectedAssistantForChannels(null);
        setChannelAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to associate channels with assistant:', error);
      setChannelAssociationSuccess(false);
      
      setTimeout(() => {
        setChannelAssociationSuccess(null);
      }, 3000);
    } finally {
      setChannelAssociationLoading(false);
    }
  };

  const handleChannelToggle = (channelName) => {
    setSelectedChannels(prev => ({
      ...prev,
      [channelName]: !prev[channelName]
    }));
  };

  const handleAgentClick = (assistant) => {
    navigate(`/ai-assistants?assistantId=${assistant._id}`);
  };

  const getChannelIcon = (channelName) => {
    const channelIcons = {
      'whatsapp': <FaWhatsapp />,
      'facebook': <FaFacebook />,
      'messenger': <FaFacebook />,
      'twitter': <FaTwitter />,
      'slack': <FaSlack />,
      'instagram': <FaInstagram />,
      'discord': <FaDiscord />,
      'telegram': <FaTelegram />,
      'amazon': <FaAmazon />,
      'website': <FaComments />,
      'custom': <FaTools />
    };
    
    return channelIcons[channelName.toLowerCase()] || <FaComments />;
  };

  const handleCreateAIAssistant = async () => {
    if (!newAgentName.trim()) {
      alert(t('aiAssistant.nameRequired'));
      return;
    }

    setCreateLoading(true);
    setCreateSuccess(null);

    if (user && user.role === 'experience') {
      setTimeout(() => {
        const newDummyAssistant = {
          _id: `exp_asst_${Date.now()}`,
          name: newAgentName,
          prompt: newAgentPrompt || 'Default demo prompt.',
          selected_tool_ids: selectedToolIds || [],
          is_running: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setAIAssistants(prev => [...prev, newDummyAssistant]);
        setCreateSuccess(true);
        setCreateLoading(false);
        
        setTimeout(() => {
          resetCreateForm();
          setShowCreateAgentModal(false);
          setCreateSuccess(null);
        }, 1500);
      }, 500);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        '/api/ai-assistants',
        { 
          name: newAgentName, 
          prompt: newAgentPrompt, 
          selected_tool_ids: selectedToolIds 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAIAssistants(prev => [...prev, response.data]);
      setCreateSuccess(true);
      
      setTimeout(() => {
        resetCreateForm();
        setShowCreateAgentModal(false);
        setCreateSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to create AI assistant:', error);
      setCreateSuccess(false);
      
      setTimeout(() => {
        setCreateSuccess(null);
      }, 3000);
    } finally {
      setCreateLoading(false);
    }
  };

  const resetCreateForm = () => {
    setNewAgentName('');
    setNewAgentPrompt('');
    setSelectedToolIds([]);
  };

  const handleAssociateBrandClick = (assistantId) => {
    setSelectedAssistantForBrand(assistantId);
    setShowBrandSelector(true);
  };

  const associateBrandWithAssistant = async (brandId) => {
    if (!selectedAssistantForBrand) return;
    
    setBrandAssociationLoading(true);
    setBrandAssociationSuccess(null);
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/associate-brand',
        {
          brand_id: brandId,
          assistant_id: selectedAssistantForBrand
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === selectedAssistantForBrand) {
          return { ...assistant, associated_brand_id: brandId };
        }
        return assistant;
      }));
      
      setBrandAssociationSuccess(true);
      
      setTimeout(() => {
        setShowBrandSelector(false);
        setSelectedAssistantForBrand(null);
        setBrandAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to associate brand with assistant:', error);
      setBrandAssociationSuccess(false);
      
      setTimeout(() => {
        setBrandAssociationSuccess(null);
      }, 3000);
    } finally {
      setBrandAssociationLoading(false);
    }
  };

  const removeBrandAssociation = async (assistantId, brandId) => {
    setBrandAssociationLoading(true);
    setBrandAssociationSuccess(null);
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/remove-brand-association',
        {
          brand_id: brandId,
          assistant_id: assistantId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === assistantId) {
          const updatedAssistant = { ...assistant };
          delete updatedAssistant.associated_brand_id;
          return updatedAssistant;
        }
        return assistant;
      }));
      
      setBrandAssociationSuccess(true);
      
      setTimeout(() => {
        setShowBrandSelector(false);
        setSelectedAssistantForBrand(null);
        setBrandAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to remove brand association:', error);
      setBrandAssociationSuccess(false);
      
      setTimeout(() => {
        setBrandAssociationSuccess(null);
      }, 3000);
    } finally {
      setBrandAssociationLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="agents-list-wrapper">
        <div className="agents-card">
          <div className="agents-skeleton" aria-busy="true">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="agents-skeleton-row" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const validEmails = [
    userEmails.gmail_email, 
    ...(userEmails.outlook_emails || [])
  ].filter(email => email);

  // Filter out test channel
  const filteredChannels = Object.keys(availableChannels).filter(channel => channel !== 'test');

  return (
    <div className="agents-list-wrapper">
      <div className="agents-list-header">
        <span className="agents-count">
          {aiAssistants.length === 1
            ? t('aiAssistant.countOne', '1 agent')
            : t('aiAssistant.countMany', '{{total}} agents', { total: aiAssistants.length })}
        </span>
        <button
          type="button"
          className="u-btn u-btn--primary"
          onClick={() => setShowCreateAgentModal(true)}
        >
          <FaPlus /> {t('aiAssistant.createNew')}
        </button>
      </div>

      <div className="agent-cards-grid">
        {aiAssistants.map((assistant) => {
          const assistantTools = availableTools.filter(tool =>
            assistant.selected_tool_ids && assistant.selected_tool_ids.includes(tool.id)
          );
          const activeChannels = Object.entries(assistant.associated_channels || {})
            .filter(([name, isActive]) => isActive && name !== 'test')
            .map(([name]) => name);
          const associatedBrand = availableBrands.find(b => b.id === assistant.associated_brand_id);

          return (
            <div
              key={assistant._id}
              className="agent-card"
              onClick={() => handleAgentClick(assistant)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleAgentClick(assistant);
                }
              }}
            >
              <div className="agent-card-header">
                <span className="agent-card-avatar"><FaRobot /></span>
                <h4 className="agent-card-name">{assistant.name}</h4>
                {assistantTools.length > 0 && (
                  <span className="agent-tools-count" title={assistantTools.map(tool => getToolTranslation(tool.name)).join(', ')}>
                    <FaTools aria-hidden="true" /> {assistantTools.length}
                  </span>
                )}
              </div>

              {assistant.prompt && (
                <p className="agent-card-prompt">{getPromptPreview(assistant.prompt)}</p>
              )}

              {/* Bindings live ON the card — no more remote "connections" diagram */}
              <div className="agent-card-bindings">
                <span className={`agent-binding ${assistant.associated_email ? '' : 'empty'}`} title={assistant.associated_email || ''}>
                  <FaEnvelope aria-hidden="true" />
                  {assistant.associated_email || t('aiAssistant.noEmailBound', 'aucun email')}
                </span>
                <span className={`agent-binding ${activeChannels.length > 0 ? '' : 'empty'}`}>
                  <span className="agent-binding-channels">
                    {activeChannels.length > 0
                      ? activeChannels.map((channel) => (
                          <span key={channel} className="agent-channel-icon" title={channel}>
                            {getChannelIcon(channel)}
                          </span>
                        ))
                      : <>{t('aiAssistant.noChannelsBound', 'aucun canal')}</>}
                  </span>
                </span>
                {associatedBrand && (
                  <span className="agent-binding" title={associatedBrand.name}>
                    <FaTag aria-hidden="true" />
                    {associatedBrand.name}
                  </span>
                )}
              </div>

              <div className="agent-card-actions">
                {validEmails.length > 0 && (
                  <button
                    type="button"
                    className="agent-card-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssociateEmailClick(assistant._id);
                    }}
                  >
                    <FaEnvelope /> {t('aiAssistant.emailShort', 'Email')}
                  </button>
                )}
                {filteredChannels.length > 0 && (
                  <button
                    type="button"
                    className="agent-card-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssociateChannelsClick(assistant);
                    }}
                  >
                    <FaComments /> {t('aiAssistant.channelsShort', 'Canaux')}
                  </button>
                )}
                {user && user.role !== 'experience' && availableBrands.length > 0 && (
                  <button
                    type="button"
                    className="agent-card-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssociateBrandClick(assistant._id);
                    }}
                  >
                    <FaTag /> {t('aiAssistant.brandShort', 'Marque')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showEmailSelector && (
        <div className="email-selector-modal">
          <div className="email-selector-content">
            <h3>{t('aiAssistant.selectEmail')}</h3>
            
            {associationSuccess === true && (
              <div className="association-success">
                <FaCheck /> {t('aiAssistant.emailAssociatedSuccess')}
              </div>
            )}
            
            {associationSuccess === false && (
              <div className="association-error">
                <FaTimes /> {t('aiAssistant.emailAssociatedError')}
              </div>
            )}
            
            {associationSuccess === null && (
              <>
                <div className="email-options">
                  {validEmails.map((email, index) => (
                    <Button 
                      key={index}
                      variant="tertiary"
                      onClick={() => associateEmailWithAssistant(email)}
                      disabled={associationLoading}
                    >
                      {email}
                    </Button>
                  ))}
                  
                  {aiAssistants.find(a => a._id === selectedAssistantForEmail)?.associated_email && (
                    <Button 
                      variant="tertiary"
                      onClick={() => {
                        if (window.confirm(t('aiAssistant.confirmRemoveEmail'))) {
                          const assistant = aiAssistants.find(a => a._id === selectedAssistantForEmail);
                          removeEmailAssociation(selectedAssistantForEmail, assistant.associated_email);
                        }
                      }}
                      disabled={associationLoading}
                    >
                      <FaTimes /> {t('aiAssistant.removeEmail')}
                    </Button>
                  )}
                </div>
                
                <div className="modal-actions">
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setShowEmailSelector(false);
                      setSelectedAssistantForEmail(null);
                    }}
                  >
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {showChannelSelector && (
        <div className="channel-selector-modal">
          <div className="channel-selector-content">
            <h3>{t('aiAssistant.manageChannels')}</h3>
            
            {channelAssociationSuccess === true && (
              <div className="association-success">
                <FaCheck /> {t('aiAssistant.channelsAssociatedSuccess')}
              </div>
            )}
            
            {channelAssociationSuccess === false && (
              <div className="association-error">
                <FaTimes /> {t('aiAssistant.channelsAssociatedError')}
              </div>
            )}
            
            {channelAssociationSuccess === null && (
              <>
                <div className="channel-options">
                  {Object.entries(availableChannels)
                    .filter(([channelName]) => channelName !== 'test')
                    .map(([channelName, isAvailable]) => (
                      isAvailable && (
                        <div key={channelName} className="channel-option">
                          <label className="channel-toggle">
                            <input
                              type="checkbox"
                              checked={selectedChannels[channelName] || false}
                              onChange={() => handleChannelToggle(channelName)}
                              disabled={channelAssociationLoading}
                            />
                            <span className="channel-toggle-slider"></span>
                          </label>
                          <span className="channel-name">{channelName}</span>
                        </div>
                      )
                    ))}
                </div>
                
                <div className="modal-actions">
                  <Button 
                    variant="primary"
                    onClick={associateChannelsWithAssistant}
                    disabled={channelAssociationLoading}
                  >
                    {channelAssociationLoading ? (
                      <span className="spinner-icon"></span>
                    ) : (
                      t('aiAssistant.saveChannels')
                    )}
                  </Button>
                  
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setShowChannelSelector(false);
                      setSelectedAssistantForChannels(null);
                    }}
                    disabled={channelAssociationLoading}
                  >
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showBrandSelector && (
        <div className="brand-selector-modal">
          <div className="brand-selector-content">
            <h3>{t('aiAssistant.selectBrand')}</h3>
            
            {brandAssociationSuccess === true && (
              <div className="association-success">
                <FaCheck /> {t('aiAssistant.brandAssociatedSuccess')}
              </div>
            )}
            
            {brandAssociationSuccess === false && (
              <div className="association-error">
                <FaTimes /> {t('aiAssistant.brandAssociatedError')}
              </div>
            )}
            
            {brandAssociationSuccess === null && (
              <>
                <div className="brand-options">
                  {availableBrands.map((brand) => (
                    <Button 
                      key={brand.id}
                      variant="tertiary"
                      onClick={() => associateBrandWithAssistant(brand.id)}
                      disabled={brandAssociationLoading}
                      className="brand-option-button"
                    >
                      <FaTag /> {brand.name}
                      {brand.description && (
                        <span className="brand-description">{brand.description}</span>
                      )}
                    </Button>
                  ))}
                  
                  {aiAssistants.find(a => a._id === selectedAssistantForBrand)?.associated_brand_id && (
                    <Button 
                      variant="tertiary"
                      onClick={() => {
                        if (window.confirm(t('aiAssistant.confirmRemoveBrand'))) {
                          const assistant = aiAssistants.find(a => a._id === selectedAssistantForBrand);
                          removeBrandAssociation(selectedAssistantForBrand, assistant.associated_brand_id);
                        }
                      }}
                      disabled={brandAssociationLoading}
                    >
                      <FaTimes /> {t('aiAssistant.removeBrand')}
                    </Button>
                  )}
                </div>
                
                <div className="modal-actions">
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setShowBrandSelector(false);
                      setSelectedAssistantForBrand(null);
                    }}
                  >
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Agent Modal */}
      {showCreateAgentModal && (
        <div className="ai-agent-modal">
          <div className="ai-agent-modal-content">
            <h3>{t('aiAssistant.createAssistant')}</h3>
            
            {createSuccess === true && (
              <div className="association-success">
                <FaCheck /> {t('aiAssistant.createSuccessDemo')}
              </div>
            )}
            
            {createSuccess === false && (
              <div className="association-error">
                <FaTimes /> {t('aiAssistant.createError')}
              </div>
            )}
            
            {createSuccess === null && (
              <>
                <div className="form-group">
                  <label htmlFor="newAgentName">{t('aiAssistant.name')}:</label>
                  <input
                    type="text"
                    id="newAgentName"
                    value={newAgentName}
                    onChange={(e) => setNewAgentName(e.target.value)}
                    className="ai-agent-input"
                    disabled={createLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="newAgentPrompt">{t('aiAssistant.prompt')}:</label>
                  <textarea
                    id="newAgentPrompt"
                    value={newAgentPrompt}
                    onChange={(e) => setNewAgentPrompt(e.target.value)}
                    rows="4"
                    className="ai-agent-textarea"
                    disabled={createLoading}
                  />
                </div>
                
                <div className="ai-agent-tools-section">
                  <h4>{t('aiAssistant.selectTools')}</h4>
                  <div className="ai-agent-tools-grid">
                    {availableTools.map((tool) => (
                      <div key={tool.id} className="ai-agent-tool-item">
                        <input
                          type="checkbox"
                          id={`create-tool-${tool.id}`}
                          checked={selectedToolIds.includes(tool.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedToolIds(prev => [...prev, tool.id]);
                            } else {
                              setSelectedToolIds(prev => prev.filter(id => id !== tool.id));
                            }
                          }}
                          disabled={createLoading}
                        />
                        <label htmlFor={`create-tool-${tool.id}`}>
                          {getToolTranslation(tool.name)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="modal-actions">
                  <Button
                    variant="primary"
                    onClick={handleCreateAIAssistant}
                    disabled={createLoading}
                  >
                    {createLoading ? (
                      <span className="spinner-icon"></span>
                    ) : (
                      <>
                        <FaPlus /> {t('aiAssistant.create')}
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="secondary"
                    onClick={() => {
                      resetCreateForm();
                      setShowCreateAgentModal(false);
                    }}
                    disabled={createLoading}
                  >
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgentsList;